import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import usePWA from '../hooks/usePWA';

/* ═══════════════════════════════════════════════════════════════════════════
   GS Mwendo S3 — Official Download / Landing Page
   Public route: /download  (no login required)
   Detects the user's device/browser and shows the right install button.
═══════════════════════════════════════════════════════════════════════════ */

const FEATURES = [
  { icon: '💬', title: 'Class Chat',       desc: 'Real-time group messaging with file & media sharing' },
  { icon: '📰', title: 'Social Feed',      desc: 'Share posts, photos and videos with classmates' },
  { icon: '📞', title: 'Voice & Video',    desc: 'One-click audio and video calls inside the class' },
  { icon: '📴', title: 'Works Offline',    desc: 'Read messages and posts even without internet' },
  { icon: '🔔', title: 'Notifications',    desc: 'Get notified instantly for new messages' },
  { icon: '🔒', title: 'Private & Secure', desc: 'Only class members can access the platform' },
];

const STEPS = {
  android: [
    { title: 'Open in Chrome', desc: 'Visit this page in Google Chrome on your Android phone.' },
    { title: 'Tap the menu',   desc: 'Tap the ⋮ three-dot menu at the top-right of Chrome.' },
    { title: 'Add to Home Screen', desc: 'Tap "Add to Home Screen" or "Install app".' },
    { title: 'Done!',          desc: 'The GS Mwendo icon will appear on your home screen.' },
  ],
  ios: [
    { title: 'Open in Safari', desc: 'Visit this page in Safari (not Chrome or Firefox).' },
    { title: 'Tap Share ⎙',    desc: 'Tap the Share button at the bottom of the screen.' },
    { title: 'Add to Home Screen', desc: 'Scroll down and tap "Add to Home Screen".' },
    { title: 'Done!',          desc: 'Tap Add — the app icon is now on your home screen.' },
  ],
  desktop: [
    { title: 'Open in Chrome or Edge', desc: 'Visit this page in Google Chrome or Microsoft Edge.' },
    { title: 'Click install icon', desc: 'Click the ⊕ install icon in the address bar (right side).' },
    { title: 'Click Install',      desc: 'Confirm by clicking Install in the popup.' },
    { title: 'Done!',              desc: 'The app opens in its own window — find it in your apps.' },
  ],
};

/* ── Small reusable components ──────────────────────────────────────────── */

const Badge = ({ children, color = 'bg-white/10 text-green-200' }) => (
  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${color}`}>
    {children}
  </span>
);

const FeatureCard = ({ icon, title, desc }) => (
  <div className="glass rounded-2xl p-4 sm:p-5 card-lift">
    <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">{icon}</div>
    <h3 className="text-white font-bold text-sm sm:text-base mb-1">{title}</h3>
    <p className="text-green-200 text-xs sm:text-sm leading-relaxed">{desc}</p>
  </div>
);

const StepItem = ({ num, title, desc }) => (
  <div className="flex items-start gap-3 sm:gap-4">
    <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-whatsapp-green flex items-center justify-center text-white font-black text-sm shadow-md animate-pulse-ring">
      {num}
    </div>
    <div className="pt-0.5">
      <p className="text-white font-semibold text-sm sm:text-base">{title}</p>
      <p className="text-green-200 text-xs sm:text-sm mt-0.5">{desc}</p>
    </div>
  </div>
);

/* ── Main Page ──────────────────────────────────────────────────────────── */
export default function Download() {
  const { canInstall, isInstalled, isIOS, isAndroid, triggerInstall } = usePWA();
  const [activeTab, setActiveTab] = useState(isIOS ? 'ios' : isAndroid ? 'android' : 'desktop');
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(isInstalled);

  const handleInstall = async () => {
    setInstalling(true);
    const ok = await triggerInstall();
    if (ok) setInstalled(true);
    setInstalling(false);
  };

  const tabs = [
    { id: 'android', label: 'Android', icon: '🤖' },
    { id: 'ios',     label: 'iPhone',  icon: '🍎' },
    { id: 'desktop', label: 'Desktop', icon: '💻' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-whatsapp-darker via-[#0a6b60] to-whatsapp-dark">

      {/* ── Top nav bar ─────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-whatsapp-green rounded-xl flex items-center justify-center shadow-md">
              <span className="text-white font-black text-xs sm:text-sm">S3</span>
            </div>
            <div>
              <span className="text-white font-black text-sm sm:text-base">GS Mwendo R</span>
              <span className="hidden sm:inline text-green-300 text-xs ml-2">Class S3</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login"
              className="text-green-200 hover:text-white text-xs sm:text-sm font-medium transition-colors px-3 py-1.5 rounded-lg hover:bg-white/10">
              Sign In
            </Link>
            {canInstall && !installed && (
              <button onClick={handleInstall} disabled={installing}
                className="bg-whatsapp-green hover:bg-green-400 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl transition-colors shadow flex items-center gap-1.5 disabled:opacity-60">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                </svg>
                {installing ? 'Installing…' : 'Install'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero section ────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-10 sm:pb-16 text-center">
        {/* App icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 sm:w-28 sm:h-28 bg-white rounded-3xl shadow-2xl mb-6 sm:mb-8">
          <div className="text-center">
            <div className="text-whatsapp-darker font-black text-xl sm:text-3xl leading-none">GS</div>
            <div className="text-whatsapp-green font-black text-base sm:text-xl leading-none">S3</div>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap justify-center gap-2 mb-4 sm:mb-5">
          <Badge color="bg-whatsapp-green/20 text-green-300">🆓 Free</Badge>
          <Badge>📱 Android</Badge>
          <Badge>🍎 iPhone</Badge>
          <Badge>💻 Desktop</Badge>
          <Badge>📴 Works Offline</Badge>
        </div>

        <h1 className="text-3xl xs:text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
          GS Mwendo S3
        </h1>
        <p className="text-green-200 text-base sm:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
          The official class platform for <strong className="text-white">GS Mwendo R — S3</strong>.
          Chat, share, and connect — from any device, anywhere.
        </p>

        {/* ── Primary CTA ───────────────────────────────────────────────── */}
        {installed ? (
          <div className="inline-flex flex-col items-center gap-3">
            <div className="flex items-center gap-2 bg-green-500/20 border border-green-400/30 text-green-300 px-5 py-3 rounded-2xl text-sm font-semibold">
              <span className="text-xl">✅</span> App is already installed!
            </div>
            <Link to="/chat"
              className="inline-flex items-center gap-2 bg-whatsapp-green hover:bg-green-400 text-white font-black py-3.5 px-8 rounded-2xl text-base transition-colors shadow-lg">
              Open App →
            </Link>
          </div>
        ) : canInstall ? (
          /* Native install available — show big button */
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={handleInstall} disabled={installing}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-whatsapp-green hover:bg-green-400 text-white font-black py-4 px-8 sm:px-10 rounded-2xl text-base sm:text-lg transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5 disabled:opacity-60">
              {installing ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Installing…
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
                  </svg>
                  Install App — Free
                </>
              )}
            </button>
            <Link to="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 glass text-white font-bold py-4 px-8 rounded-2xl text-base transition-all hover:bg-white/20">
              Use in Browser →
            </Link>
          </div>
        ) : isIOS ? (
          /* iOS — manual Safari instructions */
          <div className="flex flex-col items-center gap-4">
            <div className="glass rounded-2xl px-5 py-4 text-center max-w-sm">
              <p className="text-white font-bold mb-1">📱 iPhone / iPad</p>
              <p className="text-green-200 text-sm">Open this page in <strong className="text-white">Safari</strong>, then tap</p>
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2 mt-2 text-white font-semibold text-sm">
                Share ⎙ → Add to Home Screen
              </div>
            </div>
            <Link to="/login"
              className="inline-flex items-center gap-2 bg-whatsapp-green hover:bg-green-400 text-white font-black py-3.5 px-8 rounded-2xl text-base transition-colors shadow-lg">
              Use in Browser →
            </Link>
          </div>
        ) : (
          /* Generic fallback */
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#how-to-install"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-whatsapp-green hover:bg-green-400 text-white font-black py-4 px-8 rounded-2xl text-base transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
              </svg>
              Download / Install
            </a>
            <Link to="/login"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 glass text-white font-bold py-4 px-8 rounded-2xl text-base transition-all hover:bg-white/20">
              Use in Browser →
            </Link>
          </div>
        )}

        <p className="text-green-300/60 text-xs mt-4">
          No app store needed · Free · Runs on any device
        </p>
      </section>

      {/* ── Mockup strip ────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 sm:pb-16">
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3 justify-items-center">
          {[
            { w: 'w-12 h-20',  label: 'Watch',   icon: '⌚' },
            { w: 'w-16 h-28',  label: 'Phone',   icon: '📱' },
            { w: 'w-20 h-28',  label: 'Tablet',  icon: '📲', cls: 'hidden sm:flex' },
            { w: 'w-28 h-20',  label: 'Laptop',  icon: '💻', cls: 'hidden sm:flex' },
            { w: 'w-32 h-20',  label: 'Desktop', icon: '🖥️', cls: 'hidden sm:flex' },
          ].map(d => (
            <div key={d.label} className={`${d.cls || 'flex'} flex-col items-center gap-1.5`}>
              <div className={`${d.w} glass rounded-xl flex items-center justify-center text-2xl sm:text-3xl`}>
                {d.icon}
              </div>
              <span className="text-green-300 text-xxs sm:text-xs font-medium">{d.label}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-green-200 text-xs sm:text-sm mt-4 font-medium">
          Works on every screen — smartwatch to 4K desktop
        </p>
      </section>

      {/* ── Features grid ───────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        <h2 className="text-center text-white font-black text-2xl sm:text-3xl mb-6 sm:mb-8">
          Everything your class needs
        </h2>
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {FEATURES.map(f => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      {/* ── How to install ──────────────────────────────────────────────── */}
      <section id="how-to-install" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <h2 className="text-center text-white font-black text-2xl sm:text-3xl mb-2">
          How to install
        </h2>
        <p className="text-center text-green-200 text-sm mb-6 sm:mb-8">
          No app store, no payment — install directly from your browser in seconds.
        </p>

        {/* Tabs */}
        <div className="flex rounded-2xl overflow-hidden border border-white/10 mb-6 sm:mb-8 max-w-sm mx-auto">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                activeTab === tab.id
                  ? 'bg-whatsapp-green text-white'
                  : 'glass text-green-300 hover:text-white'
              }`}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="max-w-lg mx-auto glass rounded-2xl p-5 sm:p-6 space-y-5 sm:space-y-6">
          {STEPS[activeTab].map((s, i) => (
            <StepItem key={i} num={i + 1} title={s.title} desc={s.desc} />
          ))}
        </div>

        {/* Install CTA under steps */}
        {canInstall && !installed && (
          <div className="text-center mt-6 sm:mt-8">
            <button onClick={handleInstall} disabled={installing}
              className="inline-flex items-center gap-2 bg-whatsapp-green hover:bg-green-400 text-white font-black py-3.5 px-8 rounded-2xl text-base transition-all shadow-xl hover:-translate-y-0.5 disabled:opacity-60">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
              </svg>
              {installing ? 'Installing…' : 'Install Now — Free'}
            </button>
          </div>
        )}
      </section>

      {/* ── PWA badges / info ───────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
        <div className="glass rounded-3xl p-6 sm:p-10 text-center">
          <h2 className="text-white font-black text-xl sm:text-2xl mb-2">
            This is a Progressive Web App (PWA)
          </h2>
          <p className="text-green-200 text-sm sm:text-base max-w-xl mx-auto mb-6 sm:mb-8">
            A PWA looks and feels exactly like a native app you download from an app store —
            but you install it straight from the website. No Play Store or App Store required.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-2xl mx-auto">
            {[
              { icon: '⚡', t: 'Instant load', d: 'Cached for speed' },
              { icon: '📴', t: 'Offline ready', d: 'Works without Wi-Fi' },
              { icon: '📲', t: 'Home screen', d: 'Like a real app icon' },
              { icon: '🔄', t: 'Auto-updates', d: 'Always the latest version' },
            ].map(b => (
              <div key={b.t} className="bg-white/5 rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl mb-1.5">{b.icon}</div>
                <p className="text-white font-bold text-xs sm:text-sm">{b.t}</p>
                <p className="text-green-300 text-xxs sm:text-xs mt-0.5">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/10 py-8 sm:py-10 text-center px-4">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-8 h-8 bg-whatsapp-green rounded-lg flex items-center justify-center">
            <span className="text-white font-black text-xs">S3</span>
          </div>
          <span className="text-white font-bold">GS Mwendo R</span>
        </div>
        <div className="flex flex-wrap justify-center gap-4 text-xs text-green-300 mb-4">
          <Link to="/login"    className="hover:text-white transition-colors">Sign In</Link>
          <Link to="/download" className="hover:text-white transition-colors">Download</Link>
          <Link to="/install"  className="hover:text-white transition-colors">Install Guide</Link>
        </div>
        <p className="text-green-400/50 text-xs">© 2024 GS Mwendo R — Class S3. All rights reserved.</p>
      </footer>

      <footer className="border-t border-white/10 py-8 text-center px-4">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-8 h-8 bg-whatsapp-green rounded-lg flex items-center justify-center">
            <span className="text-white font-black text-xs">S3</span>
          </div>
          <span className="text-white font-bold">GS Mwendo R</span>
        </div>
        <div className="flex flex-wrap justify-center gap-4 text-xs text-green-300 mb-3">
          <Link to="/login"    className="hover:text-white transition-colors">Sign In</Link>
          <Link to="/download" className="hover:text-white transition-colors">Download</Link>
          <Link to="/install"  className="hover:text-white transition-colors">Install Guide</Link>
        </div>
        <p className="text-green-300/60 text-xs">
          Developed by <strong className="text-whatsapp-green">Jean Paul</strong> to My classmate —{' '}
          <span className="text-pink-400 font-semibold">I love You Too</span> ❤️ · {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
}
